import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Divider,
  FormGroup,
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { isEqual } from "lodash";
import { useState } from "react";

import { ServiceLineScheduleConfig } from "@caredna-marlin/utils/configurationTypes";

import Help from "~/components/common/Help";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import VisitWeightConfig from "./VisitWeightConfig";

import { ServiceLine } from "~/providers/OrgConfigProvider";

type Props = {
  serviceLine: ServiceLine,
};

const bufferOptions = [0, 5, 10, 15, 20, 25, 30];
const autoschedulerGroupOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const ConfigureServiceLine = ({ serviceLine }: Props) => {
  const parsedConfig = serviceLine.schedulingConfig;

  const [local, setLocal] = useState<ServiceLineScheduleConfig>(parsedConfig);
  const [updateServiceLine, { loading }] = useMutation(UPDATE_SERVICE_LINE);

  const noLocalChanges = isEqual(local, parsedConfig);

  const onSave = async () => {
    await updateServiceLine({
      variables: {
        serviceLineId: serviceLine.id,
        schedulingConfig: local,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  return <Card sx={{ height: "100%", pl: 1 }}>
    <CardContent>
      <Grid container sx={{ alignItems: "center" }}>
        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 4 }}>
          Buffer Between Visits
          <Help text="Automatically added between each visit, to allow for unexpected delays, worse than expected traffic, visits that run over, etc." />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <TextField
            select
            size="small"
            sx={{ width: "8em" }}
            value={local.minutesOfBufferTime}
            onChange={e => setLocal({ ...local, minutesOfBufferTime: parseInt(e.target.value)})}
          >
            {bufferOptions.map((option) => <MenuItem key={option} value={option}>{option} Minutes</MenuItem>)}
          </TextField>
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1, mb: 1 }}></Divider>
        </Grid>
        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 4 }}>
          Ignore Drive
          <Help text="If checked, visits will be scheduled directly at the start/end of the shift, without leaving time for driving. The provider may have to make their initial / final drive outside their scheduled hours." />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <FormGroup sx={{ pt: 1, pb: 1 }}>
            <FormControlLabel
              checked={local.ignoreDriveTimeShiftStart}
              control={<Checkbox sx={{ pt: 0, pb: 0 }} />}
              label="Start Of Shift"
              onChange={(e: any) => setLocal({ ...local, ignoreDriveTimeShiftStart: e.target.checked})}
            />
            <FormControlLabel
              checked={local.ignoreDriveTimeShiftEnd}
              control={<Checkbox sx={{ pt: 0, pb: 0 }} />}
              label="End Of Shift"
              onChange={(e: any) => setLocal({ ...local, ignoreDriveTimeShiftEnd: e.target.checked})}
            />
          </FormGroup>
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1, mb: 1 }}></Divider>
        </Grid>
        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 4 }}>
          Visit Scoring
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <VisitWeightConfig
            weights={local.scoreWeights}
            setWeights={scoreWeights => setLocal({...local, scoreWeights})}
          />
        </Grid>
        <PermissionOrHide permission={Permission.debugApps}>
          <>
            <Grid size={12}>
              <Divider sx={{ mt: 1, mb: 1 }}></Divider>
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3, xl: 4 }}>
              Use Autoscheduler
              <Help text="Enable autoscheduler setup for this service line. New episodes will open clinical constraints first." />
            </Grid>
            <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
              <FormGroup sx={{ pt: 1, pb: 1 }}>
                <FormControlLabel
                  checked={local.useAutoscheduler}
                  control={<Checkbox sx={{ pt: 0, pb: 0 }} />}
                  label="Enable Autoscheduler"
                  onChange={(e: any) => setLocal({ ...local, useAutoscheduler: e.target.checked})}
                />
              </FormGroup>
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 3, xl: 4 }}>
              Autoscheduler Groups
              <Help text="Number of autoscheduler groups for this service line. The autoscheduler will attempt to schedule all episodes in the same group with the same providers." />
            </Grid>
            <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
              <TextField
                select
                size="small"
                sx={{ width: "8em" }}
                value={local.autoschedulerGroups}
                disabled={!local.useAutoscheduler}
                onChange={e => setLocal({ ...local, autoschedulerGroups: parseInt(e.target.value) })}
              >
                {autoschedulerGroupOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        </PermissionOrHide>
      </Grid>
    </CardContent>
    <CardActions>
      <Button variant="contained" size="small" color="primary" disabled={noLocalChanges} onClick={onSave} loading={loading}>
        Save
      </Button>
      <Button variant="outlined" size="small" color="secondary" disabled={loading} onClick={() => setLocal(parsedConfig)}>
        Reset
      </Button>
    </CardActions>
  </Card>
}

export default ConfigureServiceLine;

const UPDATE_SERVICE_LINE = gql(`
  mutation UpdateServiceLine(
    $serviceLineId: Int!
    $schedulingConfig: ServiceLineSchedulingConfig!
  )
  @permission(requires: manageOrgConfig)
  {
    updateServiceLine(
      id: $serviceLineId
      schedulingConfig: $schedulingConfig
    ) {
      id
      name
      schedulingConfig
    }
  }
`);
